import React, { useCallback, useEffect, useRef, useState } from 'react';
import './TabBar.css';

const TabBar = ({ tabs, activeTab, setActiveTab, closeTab, sessionId }) => {
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: false });

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    setScrollState({
      canScrollLeft: element.scrollLeft > 1,
      canScrollRight: element.scrollLeft < maxScrollLeft - 1,
    });
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;

    const frameId = requestAnimationFrame(updateScrollState);
    element.addEventListener('scroll', updateScrollState, { passive: true });

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateScrollState)
      : null;
    resizeObserver?.observe(element);
    window.addEventListener('resize', updateScrollState);

    return () => {
      cancelAnimationFrame(frameId);
      element.removeEventListener('scroll', updateScrollState);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [tabs, sessionId, updateScrollState]);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 150, behavior: 'smooth' });
    }
  };

  return (
    <div className="tab-bar-container">
      {scrollState.canScrollLeft && (
        <button type="button" className="tab-bar-arrow" aria-label="Scroll tabs left" onClick={() => scroll(-1)}>‹</button>
      )}
      <div className="tab-bar-scroll" ref={scrollRef}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`tab-item ${activeTab === tab.id ? 'tab-active' : ''} ${tab.id === 'main' ? 'tab-main' : ''} ${tab.loading ? 'tab-loading-indicator' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-label">{tab.label}</span>
            <span className={`tab-dot ${activeTab === tab.id ? 'tab-dot-active' : 'tab-dot-offline'}`}></span>
            {tab.closable && (
              <button
                type="button"
                className="tab-close"
                aria-label={`Close ${tab.label} flow`}
                title="Close flow"
                onClick={(event) => {
                  event.stopPropagation();
                  closeTab(tab.id);
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      {scrollState.canScrollRight && (
        <button type="button" className="tab-bar-arrow" aria-label="Scroll tabs right" onClick={() => scroll(1)}>›</button>
      )}
      {sessionId && (
        <div className="session-id-badge" title={`Session ID: ${sessionId}`}>
          <span className="session-id-badge-label">Session ID</span>
          <span className="session-id-badge-value">{sessionId}</span>
        </div>
      )}
    </div>
  );
};

export default TabBar;
